const BASE = import.meta.env.VITE_API_URL ?? "";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

// ─── Per-project URL rewriting ────────────────────────────────────────────
// The API has two URL shapes for every project-scoped resource:
//   legacy:  /api/clients/123
//   nested:  /api/workspaces/<wsId>/projects/<pId>/clients/123
//
// The frontend uses ONE module-level scope, populated by
// WorkspaceProjectProvider on mount + every time the operator switches.
// Per-project paths are auto-rewritten to the nested shape before the
// fetch runs. Anything unprefixed (e.g. /api/auth/me, /api/me/*, /webhooks/*)
// passes through unchanged.

interface ActiveScope {
  workspaceId: string;
  projectId: string;
}

let activeScope: ActiveScope | null = null;

export function setActiveApiScope(scope: ActiveScope | null): void {
  activeScope = scope;
}

// Hard-coded list so a typo can't accidentally rewrite e.g. /api/auth/login
// into a nested URL. Add to this list whenever a new per-project route
// group is mounted on the backend.
const PROJECT_SCOPED_GROUPS = [
  "clients",
  "appointments",
  "calls",
  "campaigns",
  "calendar",
  "notifications",
  "dashboard",
] as const;

// Returns the active scope so hooks can build URLs that need it (e.g.
// project-settings, where a plain "/api/settings" rewrite would collide
// with the legacy /api/settings/retell-voice family until Phase 4 moves
// those off too).
export function getActiveApiScope(): ActiveScope | null {
  return activeScope;
}

function rewriteForActiveScope(path: string): string {
  if (!activeScope) return path;
  for (const group of PROJECT_SCOPED_GROUPS) {
    const legacyPrefix = `/api/${group}`;
    if (path === legacyPrefix || path.startsWith(`${legacyPrefix}/`) || path.startsWith(`${legacyPrefix}?`)) {
      const rest = path.slice(`/api/`.length);
      return `/api/workspaces/${activeScope.workspaceId}/projects/${activeScope.projectId}/${rest}`;
    }
  }
  return path;
}

/**
 * Build a fully-resolved URL for a given API path — applies the same scope
 * rewrite as api(), but returns the URL string instead of fetching. Use
 * this for <a href> exports, <img src>, etc. where the browser does the
 * GET on its own.
 */
export function apiUrl(path: string): string {
  return `${BASE}${rewriteForActiveScope(path)}`;
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const rewritten = rewriteForActiveScope(path);
  // FormData bodies (file uploads) must NOT have a Content-Type header —
  // the browser sets it with the correct multipart boundary. Forcing
  // application/json here breaks Multer parsing on the API.
  const isFormData =
    typeof FormData !== "undefined" && init.body instanceof FormData;
  const baseHeaders = isFormData
    ? {}
    : ({ "Content-Type": "application/json" } as Record<string, string>);
  const res = await fetch(`${BASE}${rewritten}`, {
    credentials: "include",
    headers: {
      ...baseHeaders,
      ...(init.headers ?? {}),
    },
    ...init,
  });

  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // ignore non-JSON bodies
    }
    // Session expired mid-use: kick straight to /login. Skips:
    //   - /api/auth/me — useMe treats 401 as "not logged in" and
    //     RequireAuth handles the bounce
    //   - /api/sysadmin/* — 401 here just means "not a system admin",
    //     which is the expected state for every customer. The hooks
    //     under useSysadmin/useImpersonation catch + treat as inactive.
    //     Pre-Phase-A this redirect was safe because no global mount
    //     ever called /api/sysadmin/* for non-admins; with the
    //     ImpersonationBanner now in AppShell, every customer pings
    //     /api/sysadmin/impersonate on load and the auto-redirect
    //     would log them straight out.
    //   - already on /login — prevents redirect loop.
    if (
      res.status === 401 &&
      !path.includes("/api/auth/me") &&
      !path.startsWith("/api/sysadmin/") &&
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/login")
    ) {
      window.location.href = "/login?expired=1";
    }
    throw new ApiError(message, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
